import { FIDUCIAL_POINT_TYPES, DEFAULT_MEDIAN_BEAT_WINDOW_MS } from "../constants/annotator"
import { computeFiducialIntervals, getMedianBeatFrequency } from "./fiducialIntervals"
import { computeMedianBeatWaveforms } from "./medianBeatCompute"

// Median-beat serialization (v3 `medianBeats` field).
//
// Consolidates the previously-separate `medianBeatFiducialPoints` field into
// a single `medianBeats` blob that carries fiducial points AND the per-lead
// waveform samples used to compute those points, so downstream consumers can
// reproduce what the annotator saw without re-running the alignment pipeline.
//
// The heavy lifting (12-lead: kai12Analysis lookup + resample + derived
// leads; 1/6-lead: per-lead ComputeAvgMedBeat + inversion + resample) lives
// in `medianBeatCompute.js` — shared with the display views so there is one
// source of truth for how a median beat is built.
//
// Units: `waveforms` samples are stored in **millivolts (mV)**, and the
// top-level `unit: "mV"` field on the `medianBeats` blob makes this explicit
// for downstream consumers. Conversion happens here at serialize time by
// dividing each sample by `filtered.gain` (the recording's "counts per mV" —
// or 1 when samples are already mV-scaled, e.g. most turk-style JSONs).
//  - 1/6-lead: compute pipeline works in the recording's native sample unit
//    (ADC counts for ATC-origin recordings, mV for pre-scaled turk JSONs);
//    dividing by `gain` collapses both to mV.
//  - 12-lead: `kai12Analysis.medianBeats` is already mV and the payload
//    carries no `gain`; pass through with gain=1.
// Downstream consumers should treat samples as physical mV and must NOT
// apply `gain` again.
//
// Inversion: the persisted `waveforms` samples are stored in
// inversion-applied form. If the recording was marked as inverted
// (annotations.global[].isInverted === true), `computeMedianBeatWaveforms`
// runs `applyInversion` on the filtered signal before averaging, so the
// samples we write here are what the annotator displayed — not the raw
// pre-inversion signal. Downstream consumers of the ZIP should treat
// `waveforms` as the final display-space signal and must NOT re-apply the
// `isInverted` flag. (12-lead recordings ignore the inversion toggle and
// use kai12Analysis.medianBeats verbatim.)

// Enumerate beat-type slugs to serialize: every type that has ≥2 beats in the
// current annotations, since alignedBeatFromIndices needs at least two beats
// to compute a median. Noise (`x`) is excluded.
const enumerateComputableBeatTypes = (annotatorState) => {
    const beats = annotatorState?.annotations?.beats || []
    const counts = new Map()
    for (const b of beats) {
        if (!b?.t || b.t === "x") continue
        counts.set(b.t, (counts.get(b.t) || 0) + 1)
    }
    return [...counts.entries()].filter(([, n]) => n >= 2).map(([slug]) => slug)
}

// Convert typed arrays to plain arrays so the payload JSON-serializes cleanly.
const toPlainArray = (arr) => (Array.isArray(arr) ? arr : Array.from(arr || []))

// Public: for one beat-type slug, return { sampleRate, waveforms }
// where waveforms is { [lead]: number[] } in mV, or null if a median beat
// can't be computed for this slug.
export const computeMedianBeatWaveformsForType = (annotatorState, slug) => {
    const is12Lead = annotatorState?.leadCount === 12
    // kai12 median beats aren't split by beat type; only attribute them to `n`.
    if (is12Lead && slug !== "n") return null

    const computed = computeMedianBeatWaveforms(annotatorState, { slug, requireComputable: true })
    if (!computed) return null

    // 1/6-lead: divide compute-pipeline samples by counts-per-mV to write mV.
    // Turk-style JSONs already carry mV samples with gain=1, so the divide is
    // a no-op there. 12-lead: kai12Analysis.medianBeats is already mV — use 1.
    const gain = is12Lead ? 1 : (annotatorState?.filtered?.gain || annotatorState?.original?.gain || 1)

    const waveforms = {}
    for (const leadName of Object.keys(computed.waveforms)) {
        const src = computed.waveforms[leadName] || []
        if (gain === 1) {
            waveforms[leadName] = toPlainArray(src)
        } else {
            const out = new Array(src.length)
            for (let i = 0; i < src.length; i++) out[i] = src[i] / gain
            waveforms[leadName] = out
        }
    }
    return {
        sampleRate: getMedianBeatFrequency(annotatorState),
        waveforms,
    }
}

// Public: build the v3 `medianBeats` field for one recording.
//
// Shape:
//   {
//     version: 3,
//     unit: "mV",                 // waveform samples are physical millivolts
//     sampleRate,                 // same for every beat type in a recording
//     byBeatType: {
//       <slug>: {
//         window: { leftMs, rightMs },   // omitted for 12-lead
//         waveforms: { [leadName]: number[] },  // mV
//         fiducialPoints: {              // omitted if none placed
//           pointLabels: [...],
//           computedIntervals: {...},
//         },
//       },
//     },
//   }
//
// `dropFiducialTypes=[slug,...]` drops fiducial-point data for the named
// buckets only. Waveforms are always serialized regardless.
export const buildMedianBeatsField = (
    annotatorState,
    { dropFiducialTypes = null } = {},
) => {
    const is12Lead = annotatorState?.leadCount === 12
    const fpByType = annotatorState?.annotations?.medianBeatFiducialPointsByType || {}
    const droppedSet = new Set(Array.isArray(dropFiducialTypes) ? dropFiducialTypes : [])

    // Union of "types the user annotated fiducial points on" and "types we
    // can compute a median for from the beats list" — matches the request to
    // serialize waveforms for every annotated beat type for which a median
    // can be computed.
    const computable = enumerateComputableBeatTypes(annotatorState)
    const slugs = new Set([...Object.keys(fpByType), ...computable])

    const byBeatType = {}
    let sampleRate = null

    for (const slug of slugs) {
        const computed = computeMedianBeatWaveformsForType(annotatorState, slug)
        if (!computed) continue
        if (sampleRate == null) sampleRate = computed.sampleRate

        const entry = {
            waveforms: computed.waveforms,
        }
        if (!is12Lead) {
            const w = (annotatorState.settings?.medianBeatWindowByType || {})[slug]
            entry.window = {
                leftMs: w?.leftMs ?? DEFAULT_MEDIAN_BEAT_WINDOW_MS,
                rightMs: w?.rightMs ?? DEFAULT_MEDIAN_BEAT_WINDOW_MS,
            }
        }

        const pointLabels = fpByType[slug] || []
        if (!droppedSet.has(slug) && pointLabels.length > 0) {
            const intervals = computeFiducialIntervals(pointLabels)
            const computedIntervals = Object.fromEntries(
                Object.entries(intervals).filter(([, v]) => v != null)
            )
            entry.fiducialPoints = {
                pointLabels,
                computedIntervals,
            }
        }
        byBeatType[slug] = entry
    }

    return {
        version: 3,
        unit: "mV",
        sampleRate: sampleRate ?? getMedianBeatFrequency(annotatorState),
        byBeatType,
    }
}

// Convenience: for the fiducial-point legend write in save paths.
export const buildFiducialPointLegend = () => FIDUCIAL_POINT_TYPES.map((fp) => ({
    name: fp.name,
    color: fp.color,
    label: fp.slug,
}))
